import React, { type MouseEventHandler, useCallback, useState, useContext } from 'react';
import IconButton from '@atlaskit/button/icon/button';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { token } from '@atlaskit/tokens';
import { isFavoritableItemTypename } from '@jira/platform__favouriting-favourite-change-provider/src/model/utils.tsx';
import { useChangeFavorite } from '@jira/platform__favouriting-favourite-change-provider/src/view/FavoriteChangeConsumer.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	STAR,
	UNSTAR,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { AnalyticContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/controllers/analytics-context/index.tsx';
import type { StarredOLFItemAnalytics } from './JiraRecentOLFItem.types.tsx';

const starButtonLabelWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.add-to-starred-with-name',
	defaultMessage: 'Add "{name}" to starred',
	description:
		'Used as the alt text for the "add to starred" menu button item. The {name} attribute refers to the name of the entity that should be added to starred. For example, this could be a filter name like "blocked issues" and the full message would be "Add blocked issues to starred".',
});

const unstarButtonLabelWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.remove-from-starred-with-name',
	defaultMessage: 'Remove "{name}" from starred',
	description:
		'Used as the alt text for the "remove from starred" menu button item. The {name} attribute refers to the name of the entity that should be remove from starred. For example, this could be a filter name like "blocked issues" and the full message would be "Remove blocked issues from starred".',
});

export type StarJiraItemButtonProps = {
	analytics: StarredOLFItemAnalytics;
	defaultIsStarred?: boolean;
	id: string;
	name?: string;
	typename: string;
};

export function StarJiraItemButton(params: StarJiraItemButtonProps): React.JSX.Element {
	const {
		analytics: { itemId, itemType },
		defaultIsStarred = false,
		id,
		name,
		typename,
	} = params;
	const { formatMessage } = useIntl();

	if (!isFavoritableItemTypename(typename)) {
		throw new Error(`Encountered unexpected item type: ${typename}`);
	}

	const [isStarred, setIsStarred] = useState(defaultIsStarred);

	const { changeFavoriteMutation } = useChangeFavorite();

	const { fireUIAnalyticForStarrButton } = useContext(AnalyticContext);

	const handleOnClick = useCallback<MouseEventHandler>(() => {
		setIsStarred((prevIsStarred) => {
			const shouldStarJiraItem = !prevIsStarred;
			changeFavoriteMutation({ id, typename, value: shouldStarJiraItem });

			fireUIAnalyticForStarrButton({
				starType: shouldStarJiraItem ? STAR : UNSTAR,
				parentItemId: itemId,
				parentItemType: itemType,
			});

			return shouldStarJiraItem;
		});
	}, [changeFavoriteMutation, id, fireUIAnalyticForStarrButton, itemId, itemType, typename]);

	const handleOnClickWithStopPropagation = useStopPropagation(handleOnClick);

	const unstarLabel = formatMessage(unstarButtonLabelWithName, { name: name || '' });
	const starLabel = formatMessage(starButtonLabelWithName, { name: name || '' });

	return isStarred ? (
		<IconButton
			appearance="subtle"
			icon={(iconProps) => (
				<StarStarredIcon {...iconProps} color={token('color.icon.accent.yellow')} />
			)}
			onClick={handleOnClickWithStopPropagation}
			label={unstarLabel}
		/>
	) : (
		<IconButton
			appearance="subtle"
			icon={StarUnstarredIcon}
			onClick={handleOnClickWithStopPropagation}
			label={starLabel}
		/>
	);
}
